import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import {
  IDeedTemplate,
  IDinoDeedPreview,
  useDinoDeedDocx,
  useDinoDeedPreview,
} from '../hooks/reactQuerys/DeedGenerationQueries';
import { IDocument } from '../Interfaces/IDocument';
import { useAlert } from '../context/AlertContext';
import { useConfirmation } from '../context/ConfirmationContext';
import { apiErrorText } from '../Admin/ArnoTop/apiErrorText';
import { PlanQuestions } from '../Admin/ArnoTop/PlanQuestions';
import { ReaderSelection, SectionReader } from '../Admin/ArnoTop/SectionReader';
import { InlineTextEdit } from '../Admin/ArnoTop/InlineTextEdit';
import {
  editableInPlace,
  nodeAt,
  NodePath,
  replaceNodeAt,
} from '../Admin/ArnoTop/readingModel';
import { IEditorNode } from '../Admin/ArnoTop/editorModel';
import {
  freshState,
  IDeedSectionState,
  mergeRerun,
  sectionsForFile,
} from './dinoDeedModel';
import {
  needsVariantChoice,
  initialVariant,
  templateLabel,
} from './deedGeneration';

/**
 * Urkunde ganz in DiNo (#1245, Teil 48) — der zweite Weg neben »In Word erstellen«.
 *
 * Vorlage wählen, Fragen hier beantworten, die Urkunde ansehen und direkt im Text anpassen, dann
 * als Entwurf in den Vorgang. Gerechnet wird genau wie für Word (dieselbe Freigabe, Version,
 * Runtime, derselbe Dokumentplan); nur die Ausgabe ist eine .docx statt eines Word-Dokuments,
 * in das das Add-in schreibt.
 *
 * Der Entwurf kommt in die Entwurfsliste des Vorgangs wie ein hochgeladener — gespeichert wird er
 * mit dem nächsten »Speichern« des Vorgangs. Ein eigener Speicherweg daneben würde vom
 * »Speichern« der Maske wieder entfernt (die Liste gilt dort als vollständig).
 */

interface Props {
  open: boolean;
  onClose: () => void;
  idLegalTransaction: number;
  templates: IDeedTemplate[];
  initialTemplate: number | null;
  onDraft: (_document: IDocument) => void;
}

type Selection = ReaderSelection & { section: number };

const NO_CONTEXT = {
  questions: [],
  fieldLabel: () => undefined,
  sample: () => undefined,
};

const download = (fileName: string, base64: string, mimeType: string) => {
  const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const DinoDeedDialog: React.FC<Props> = ({
  open,
  onClose,
  idLegalTransaction,
  templates,
  initialTemplate,
  onDraft,
}) => {
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { previewDinoDeed, isPreviewingDinoDeed } = useDinoDeedPreview();
  const { createDinoDeedDocx, isCreatingDinoDeedDocx } = useDinoDeedDocx();

  const [chosen, setChosen] = useState<number | null>(initialTemplate);
  const template =
    templates.find(entry => entry.idDecisionTemplate === chosen) ?? null;
  const [variant, setVariant] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<IDinoDeedPreview | null>(null);
  const [states, setStates] = useState<IDeedSectionState[]>([]);
  const [dropped, setDropped] = useState<string[]>([]);
  const [selection, setSelection] = useState<Selection | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Nur die Antwort auf die letzte Anfrage zählt: wer schnell die Vorlage wechselt, bekäme sonst
  // die Urkunde der vorigen, wenn deren Rechnung später fertig wird.
  const latest = useRef(0);

  useEffect(() => {
    if (open) {
      setChosen(initialTemplate);
    }
  }, [open, initialTemplate]);

  useEffect(() => {
    setVariant(initialVariant(template));
  }, [template]);

  const run = useCallback(
    async (nextAnswers: Record<string, string>, fresh: boolean) => {
      if (!chosen) {
        return;
      }
      setError(null);
      const ticket = ++latest.current;
      try {
        // Wie beim Start in Word: eine native Vorlage kennt keine Variante — das Feld fehlt dann.
        const result = await previewDinoDeed({
          idLegalTransaction,
          idDecisionTemplate: chosen,
          ...(variant ? { variant } : {}),
          answers: nextAnswers,
        });
        if (ticket !== latest.current) {
          return;
        }
        setPreview(result);
        setSelection(null);
        if (fresh) {
          setStates(result.sections.map(freshState));
          setDropped([]);
        } else {
          setStates(previous => {
            const merged = mergeRerun(previous, result.sections);
            setDropped(merged.droppedEdited);
            return merged.states;
          });
        }
      } catch (failure) {
        if (ticket === latest.current) {
          setError(apiErrorText(failure));
        }
      }
    },
    [chosen, variant, idLegalTransaction, previewDinoDeed]
  );

  // Neue Vorlage oder Variante (`run` hängt an beiden): von vorn, ohne Antworten.
  useEffect(() => {
    if (open && chosen) {
      setAnswers({});
      void run({}, true);
    }
  }, [open, chosen, run]);

  const answer = (key: string, value: string) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    void run(next, false);
  };

  const updateNodes = (
    index: number,
    update: (_nodes: IEditorNode[]) => IEditorNode[]
  ) =>
    setStates(list =>
      list.map((state, position) =>
        position === index
          ? { ...state, nodes: update(state.nodes), edited: true }
          : state
      )
    );

  const adopt = (index: number) =>
    setStates(list =>
      list.map((state, position) =>
        position === index ? freshState(state.section) : state
      )
    );

  const file = async () => {
    if (!chosen) {
      return null;
    }
    try {
      return await createDinoDeedDocx({
        idLegalTransaction,
        idDecisionTemplate: chosen,
        sections: sectionsForFile(states),
      });
    } catch (failure) {
      showAlert({
        severity: 'error',
        text: `Die Datei ließ sich nicht erzeugen: ${apiErrorText(failure)}`,
      });
      return null;
    }
  };

  const saveAsDraft = async () => {
    const created = await file();
    if (!created) {
      return;
    }
    if (!created.idMIMEType) {
      showAlert({
        severity: 'error',
        text: 'DiNo kennt den Dateityp .docx noch nicht (Migration 20261002_1245_mimetype_docx fehlt).',
      });
      return;
    }
    onDraft({
      idDocument: 0,
      FileName: created.fileName,
      idMIMEType: created.idMIMEType,
      Data: created.data,
      Created_at: new Date().toISOString(),
      idDocumentDirectory: null,
      idLegalTransaction: null,
      perEmail: false,
      isFromTemplate: false,
    });
    showAlert({
      severity: 'success',
      text: `»${created.fileName}« steht in den Entwürfen. Mit »Speichern« im Vorgang wird sie abgelegt.`,
    });
    onClose();
  };

  /** Schließen — mit Rückfrage, wenn schon Text geändert wurde (keine stille Verwerfung). */
  const close = async () => {
    if (states.some(state => state.edited)) {
      const confirmed = await showConfirmation({
        title: 'Änderungen verwerfen?',
        message:
          'Sie haben den Text der Urkunde geändert. Ohne »Als Entwurf übernehmen« gehen die Änderungen verloren.',
        confirmButtonText: 'Verwerfen und schließen',
        abortButtonText: 'Weiter bearbeiten',
      });
      if (!confirmed) {
        return;
      }
    }
    onClose();
  };

  const questions = preview?.plan.pendingQuestions ?? [];
  const missing = preview?.plan.missingValues ?? [];
  const answered = Object.keys(answers).length;
  const editedCount = states.filter(state => state.edited).length;

  const contextFor = (index: number) => {
    const state = states[index];
    const path =
      selection?.section === index &&
      selection.kind === 'node' &&
      state &&
      editableInPlace(nodeAt(state.nodes, selection.path))
        ? selection.path
        : null;
    return {
      ...NO_CONTEXT,
      editingPath: path,
      renderEditor: (node: IEditorNode, at: NodePath) => (
        <InlineTextEdit
          node={node}
          allowFields={false}
          hint='Esc beendet das Bearbeiten'
          initialCursor={
            selection?.kind === 'node'
              ? { paragraph: selection.paragraph, offset: selection.offset }
              : undefined
          }
          onChange={paragraphs =>
            updateNodes(index, nodes => {
              const current = nodeAt(nodes, at);
              return current
                ? replaceNodeAt(nodes, at, { ...current, paragraphs })
                : nodes;
            })
          }
          onDone={() => setSelection(null)}
        />
      ),
    };
  };

  return (
    <Dialog
      open={open}
      onClose={() => void close()}
      fullScreen
      // Esc gehört dem Editor (Bearbeiten beenden); geschlossen wird über das Kreuz.
      disableEscapeKeyDown
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1 }}>
          Urkunde in DiNo erstellen
          <Typography variant='body2' color='text.secondary'>
            {preview
              ? `${preview.templateName} · Version ${preview.versionNumber}${preview.legalTransactionTitle ? ` · ${preview.legalTransactionTitle}` : ''}`
              : 'Vorlage wählen'}
          </Typography>
        </Box>
        <Button
          startIcon={<DownloadIcon />}
          disabled={!preview || isCreatingDinoDeedDocx}
          onClick={async () => {
            const created = await file();
            if (created)
              download(created.fileName, created.data, created.mimeType);
          }}
        >
          Herunterladen
        </Button>
        <Button
          variant='contained'
          startIcon={
            isCreatingDinoDeedDocx ? (
              <CircularProgress size={16} color='inherit' />
            ) : (
              <NoteAddIcon />
            )
          }
          disabled={!preview || isCreatingDinoDeedDocx}
          onClick={() => void saveAsDraft()}
        >
          Als Entwurf übernehmen
        </Button>
        <IconButton aria-label='Schließen' onClick={() => void close()}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 0 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ minHeight: '100%' }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 360 },
              flexShrink: 0,
              bgcolor: 'background.paper',
              borderRight: 1,
              borderColor: 'divider',
              p: 2,
              // Fragen und fehlende Angaben bleiben sichtbar, während man durch die Urkunde blättert.
              position: { md: 'sticky' },
              top: 0,
              alignSelf: 'flex-start',
              maxHeight: { md: 'calc(100vh - 90px)' },
              overflowY: 'auto',
            }}
          >
            <Stack spacing={2}>
              {templates.length > 1 && (
                <TextField
                  select
                  size='small'
                  label='Vorlage'
                  value={chosen ?? ''}
                  onChange={event => setChosen(Number(event.target.value))}
                >
                  {templates.map(entry => (
                    <MenuItem
                      key={entry.idDecisionTemplate}
                      value={entry.idDecisionTemplate}
                    >
                      {templateLabel(entry)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              {needsVariantChoice(template) && (
                <TextField
                  select
                  size='small'
                  label='Variante'
                  value={variant ?? ''}
                  onChange={event => setVariant(String(event.target.value))}
                >
                  {(template?.variants ?? []).map(entry => (
                    <MenuItem key={entry} value={entry}>
                      {entry}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <Box>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Fragen
                </Typography>
                {questions.length > 0 ? (
                  <PlanQuestions
                    questions={questions}
                    answers={answers}
                    onAnswer={answer}
                    disabled={isPreviewingDinoDeed}
                    stacked
                  />
                ) : (
                  <Typography variant='body2' color='text.secondary'>
                    {preview ? 'Alle Fragen sind beantwortet.' : '…'}
                  </Typography>
                )}
                {answered > 0 && (
                  <Button
                    size='small'
                    sx={{ mt: 1 }}
                    disabled={isPreviewingDinoDeed}
                    onClick={() => {
                      setAnswers({});
                      void run({}, editedCount === 0);
                    }}
                  >
                    Antworten zurücksetzen
                  </Button>
                )}
              </Box>

              {missing.length > 0 && (
                <Alert severity='warning'>
                  <AlertTitle>
                    {missing.length === 1
                      ? 'Eine Angabe fehlt im Vorgang'
                      : `${missing.length} Angaben fehlen im Vorgang`}
                  </AlertTitle>
                  <Stack component='ul' sx={{ m: 0, pl: 2 }}>
                    {missing.slice(0, 12).map((entry, index) => (
                      <li key={index}>
                        <Typography variant='body2'>
                          {entry.fieldName ?? entry.label ?? entry.selector}
                          {entry.section ? ` (${entry.section})` : ''}
                        </Typography>
                      </li>
                    ))}
                  </Stack>
                  <Typography variant='caption' display='block' sx={{ mt: 1 }}>
                    Im Text rot markiert. Im Vorgang nachtragen und neu rechnen
                    — oder hier direkt in den Text schreiben.
                  </Typography>
                </Alert>
              )}

              {dropped.length > 0 && (
                <Alert severity='info' onClose={() => setDropped([])}>
                  Nach der Antwort nicht mehr vorgesehen, Ihre Änderungen darin
                  entfallen: {dropped.join(', ')}
                </Alert>
              )}

              <Divider />
              <Typography variant='caption' color='text.secondary'>
                In den Text klicken, um ihn zu ändern. Der Entwurf kommt als
                Word-Datei in die Entwürfe des Vorgangs und wird mit »Speichern«
                abgelegt.
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ flexGrow: 1, p: { xs: 1, md: 4 }, overflow: 'auto' }}>
            {error && (
              <Alert severity='error' sx={{ mb: 2, maxWidth: 900, mx: 'auto' }}>
                {error}
              </Alert>
            )}
            <Box
              sx={{
                maxWidth: 900,
                mx: 'auto',
                bgcolor: 'background.paper',
                boxShadow: 1,
                px: { xs: 2, md: 8 },
                py: { xs: 2, md: 6 },
                minHeight: 400,
                position: 'relative',
                opacity: isPreviewingDinoDeed ? 0.6 : 1,
              }}
              data-testid='dino-deed-document'
            >
              {isPreviewingDinoDeed && !preview && <CircularProgress />}
              {states.map((state, index) => (
                <Box key={state.section.blockId ?? index} sx={{ mb: 3 }}>
                  <Stack
                    direction='row'
                    spacing={1}
                    alignItems='center'
                    sx={{ mb: 0.5 }}
                  >
                    <Typography variant='overline' color='text.secondary'>
                      {state.section.label}
                    </Typography>
                    {state.edited && (
                      <Chip
                        size='small'
                        variant='outlined'
                        label='von Hand geändert'
                      />
                    )}
                    {state.planChanged && (
                      <>
                        <Chip
                          size='small'
                          color='warning'
                          label='Der Plan sieht jetzt anderen Text vor'
                        />
                        <Button size='small' onClick={() => adopt(index)}>
                          Neu übernehmen
                        </Button>
                      </>
                    )}
                  </Stack>
                  <SectionReader
                    nodes={state.nodes}
                    context={contextFor(index)}
                    selection={selection?.section === index ? selection : null}
                    onSelect={next => setSelection({ ...next, section: index })}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
